// Note am using Courcess as  Subject and Division as Department
import React, { useEffect, useMemo, useState } from "react";

import {
    Card,
    Table,
    Button,
    Input,
    Space,
    Typography,
    Tag,
    Tooltip,
    Empty,
    Spin,
    Modal,
    Row,
    Col
} from "antd";

import {
    BookOutlined,
    SearchOutlined,
    DeleteOutlined,
    ExclamationCircleFilled,
    AppstoreOutlined,
    DatabaseOutlined
} from "@ant-design/icons";

import Swal from "sweetalert2";

import api from "../../../api/api";

const { Title, Text } = Typography;
const { confirm } = Modal;

const COLORS = {
    primary: "#0F5B78",
    primaryHover: "#0B465D",
    secondary: "#365B6D",
    navy: "#172B3A",
    blue: "#2778A8",
    black: "#071014",
    success: "#16805C",
    warning: "#C88719",
    error: "#C44545",
    muted: "#647482",
    border: "#DCE4E8",
    page: "#F4F7F9",
    white: "#FFFFFF",
    soft: "#F8FAFB",
    lightBlue: "#EAF4F7"
};

const SubjectViewList = () => {
    const token = localStorage.getItem("access");

    const [subjects, setSubjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState("");

    const fetchSubjects = async () => {
        if (!token) {
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const config = {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            };

            const response = await api.get(
                "api/users/subjects/",
                config
            );

            setSubjects(
                Array.isArray(response.data)
                    ? response.data
                    : []
            );
        } catch (error) {
            console.error(error);

            Swal.fire({
                icon: "error",
                title: "Failed to Load Courses",
                text: "Unable to load Course information. Please try again.",
                confirmButtonColor: COLORS.primary
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSubjects();
    }, []);

    const handleDelete = (id) => {
        confirm({
            title: "Delete Course",
            icon: (
                <ExclamationCircleFilled
                    style={{
                        color: COLORS.error
                    }}
                />
            ),
            content:
                "Are you sure you want to delete this Course? This action cannot be undone.",
            okText: "Delete Course",
            okType: "danger",
            cancelText: "Cancel",
            centered: true,
            async onOk() {
                try {
                    const config = {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    };

                    await api.delete(
                        `api/users/subjects/${id}/`,
                        config
                    );

                    setSubjects((previous) =>
                        previous.filter(
                            (item) => item.id !== id
                        )
                    );

                    Swal.fire({
                        icon: "success",
                        title: "Course Deleted",
                        text: "The Course was deleted successfully.",
                        timer: 1800,
                        showConfirmButton: false
                    });
                } catch (error) {
                    console.error(error);

                    Swal.fire({
                        icon: "error",
                        title: "Delete Failed",
                        text: "Unable to delete Course. Please try again.",
                        confirmButtonColor: COLORS.primary
                    });
                }
            }
        });
    };

    const filteredSubjects = useMemo(() => {
        const keyword = searchText.trim().toLowerCase();

        if (!keyword) {
            return subjects;
        }

        return subjects.filter((item) =>
            item.name
                ?.toLowerCase()
                .includes(keyword) ||
            item.division_assigned_name?.name
                ?.toLowerCase()
                .includes(keyword) ||
            String(item.id).includes(keyword)
        );
    }, [subjects, searchText]);

    const columns = [
        {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 90,
            align: "center",
            sorter: (a, b) => a.id - b.id,

            render: (id) => (
                <Tag
                    className="course-id-tag"
                    style={{
                        minWidth: 48,
                        textAlign: "center",
                        fontWeight: 700,
                        borderRadius: 7,
                        padding: "3px 9px",
                        background: COLORS.lightBlue,
                        border: `1px solid ${COLORS.border}`,
                        color: COLORS.primary
                    }}
                >
                    #{id}
                </Tag>
            )
        },

        {
            title: "Course Name",
            dataIndex: "name",
            key: "name",
            sorter: (a, b) =>
                (a.name || "").localeCompare(
                    b.name || ""
                ),

            render: (name) => (
                <div className="course-name-wrapper">
                    <div className="course-icon-box">
                        <BookOutlined />
                    </div>

                    <div className="course-name-content">
                        <Text
                            strong
                            className="course-name-text"
                        >
                            {name || "Unnamed Course"}
                        </Text>

                        <Text
                            className="course-sub-label"
                        >
                            Course
                        </Text>
                    </div>
                </div>
            )
        },

        {
            title: "Department / Division",
            key: "division",

            sorter: (a, b) =>
                (
                    a.division_assigned_name?.name ||
                    ""
                ).localeCompare(
                    b.division_assigned_name?.name ||
                    ""
                ),

            render: (_, record) => (
                record.division_assigned_name ? (
                    <Tag
                        className="division-tag"
                        icon={<AppstoreOutlined />}
                    >
                        {
                            record
                                .division_assigned_name
                                .name
                        }
                    </Tag>
                ) : (
                    <Tag
                        className="no-division-tag"
                    >
                        No Division Assigned
                    </Tag>
                )
            )
        },

        {
            title: "Actions",
            key: "actions",
            width: 150,
            align: "center",

            render: (_, record) => (
                <Tooltip title="Delete Course">
                    <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() =>
                            handleDelete(record.id)
                        }
                        className="delete-course-button"
                    >
                        Delete
                    </Button>
                </Tooltip>
            )
        }
    ];

    const paginationConfig = {
        pageSize: 8,
        showSizeChanger: true,
        pageSizeOptions: [
            "5",
            "8",
            "10",
            "20"
        ],
        showQuickJumper: true,

        showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} Courses`
    };

    const SearchBar = () => (
        <Input
            allowClear
            size="large"
            prefix={
                <SearchOutlined
                    style={{
                        color: COLORS.primary
                    }}
                />
            }
            placeholder="Search Course, department or ID..."
            value={searchText}
            onChange={(e) =>
                setSearchText(e.target.value)
            }
            className="course-search-input"
        />
    );

    return (
        <div className="course-page-container">

            <Card
                bordered={false}
                className="course-main-card"
                bodyStyle={{
                    padding: 0
                }}
            >

                {/* Header */}

                <div className="course-header">

                    <div className="course-header-stripe" />

                    <div className="course-header-content">

                        <div className="course-header-left">

                            <div className="course-header-icon">
                                <BookOutlined />
                            </div>

                            <div>
                                <Title
                                    level={3}
                                    className="course-page-title"
                                >
                                    Courses
                                </Title>

                                <Text className="course-page-subtitle">
                                    Manage and monitor all courses
                                    within the academic system.
                                </Text>
                            </div>

                        </div>

                        <div className="course-header-stat">
                            <DatabaseOutlined />

                            <div>
                                <span className="course-stat-label">
                                    Total Courses
                                </span>

                                <strong>
                                    {subjects.length}
                                </strong>
                            </div>
                        </div>

                    </div>
                </div>


                {/* Controls */}

                <div className="course-controls">

                    <Row
                        gutter={[
                            16,
                            16
                        ]}
                        align="middle"
                        justify="space-between"
                    >

                        <Col
                            xs={24}
                            sm={24}
                            md={15}
                            lg={16}
                            xl={17}
                        >
                            <SearchBar />
                        </Col>

                        <Col
                            xs={24}
                            sm={24}
                            md={9}
                            lg={8}
                            xl={7}
                        >
                            <div className="course-summary">

                                <span className="summary-dot" />

                                <span>
                                    Showing
                                </span>

                                <strong>
                                    {filteredSubjects.length}
                                </strong>

                                <span>
                                    {filteredSubjects.length === 1
                                        ? "Course"
                                        : "Courses"}
                                </span>

                            </div>
                        </Col>

                    </Row>

                </div>


                {/* Table */}

                <div className="course-table-container">

                    <Spin
                        spinning={loading}
                        size="large"
                        tip="Loading Courses..."
                    >

                        <Table
                            rowKey="id"
                            columns={columns}
                            dataSource={filteredSubjects}
                            pagination={paginationConfig}
                            bordered={false}
                            scroll={{
                                x: 700
                            }}

                            locale={{
                                emptyText: (
                                    <Empty
                                        image={
                                            Empty.PRESENTED_IMAGE_SIMPLE
                                        }
                                        description={
                                            <span className="empty-description">
                                                {searchText
                                                    ? "No Courses match your search."
                                                    : "No Courses Available"}
                                            </span>
                                        }
                                    />
                                )
                            }}

                            rowClassName={(_, index) =>
                                index % 2 === 0
                                    ? "table-row-light"
                                    : "table-row-white"
                            }
                        />

                    </Spin>

                </div>

            </Card>


            <style>
                {`
                    .course-page-container {
                        padding: 24px;
                        background: ${COLORS.page};
                        min-height: 100%;
                        box-sizing: border-box;
                    }

                    .course-main-card {
                        border-radius: 16px !important;
                        overflow: hidden;
                        border: 1px solid ${COLORS.border};
                        box-shadow:
                            0 10px 30px rgba(23, 43, 58, 0.07);
                        background: ${COLORS.white};
                    }

                    .course-header {
                        position: relative;
                        overflow: hidden;
                        background:
                            linear-gradient(
                                135deg,
                                #071014 0%,
                                #10242D 52%,
                                #0F5B78 100%
                            );
                        color: #ffffff;
                    }

                    .course-header::before {
                        content: "";
                        position: absolute;
                        inset: -40%;
                        pointer-events: none;
                        background:
                            repeating-linear-gradient(
                                135deg,
                                rgba(255,255,255,0.035) 0px,
                                rgba(255,255,255,0.035) 2px,
                                transparent 2px,
                                transparent 25px
                            );
                        transform: rotate(-5deg);
                        animation:
                            courseHeaderStripes 18s linear infinite;
                    }

                    .course-header-stripe {
                        position: absolute;
                        left: 0;
                        top: 0;
                        bottom: 0;
                        width: 5px;
                        background: #29A3C6;
                        box-shadow:
                            0 0 18px rgba(41, 163, 198, 0.65);
                        z-index: 2;
                    }

                    .course-header-content {
                        position: relative;
                        z-index: 3;
                        min-height: 112px;
                        padding: 22px 28px;
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 20px;
                    }

                    .course-header-left {
                        display: flex;
                        align-items: center;
                        gap: 16px;
                        min-width: 0;
                    }

                    .course-header-icon {
                        width: 52px;
                        height: 52px;
                        flex: 0 0 52px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 13px;
                        background:
                            rgba(255,255,255,0.10);
                        border:
                            1px solid rgba(255,255,255,0.16);
                        color: #7DDEFF;
                        font-size: 25px;
                        box-shadow:
                            inset 0 0 18px rgba(255,255,255,0.03),
                            0 8px 20px rgba(0,0,0,0.16);
                    }

                    .course-page-title {
                        margin: 0 !important;
                        color: #ffffff !important;
                        font-size: 25px !important;
                        font-weight: 700 !important;
                        letter-spacing: -0.3px;
                    }

                    .course-page-subtitle {
                        display: block;
                        margin-top: 3px;
                        color: #BFD0D8 !important;
                        font-size: 13px;
                    }

                    .course-header-stat {
                        min-width: 155px;
                        display: flex;
                        align-items: center;
                        gap: 11px;
                        padding: 10px 14px;
                        border-radius: 10px;
                        background:
                            rgba(255,255,255,0.07);
                        border:
                            1px solid rgba(255,255,255,0.12);
                    }

                    .course-header-stat > .anticon {
                        color: #69CDE9;
                        font-size: 21px;
                    }

                    .course-header-stat > div {
                        display: flex;
                        flex-direction: column;
                    }

                    .course-stat-label {
                        color: #9FB5BF;
                        font-size: 11px;
                        text-transform: uppercase;
                        letter-spacing: 0.7px;
                    }

                    .course-header-stat strong {
                        color: #ffffff;
                        font-size: 19px;
                        line-height: 1.3;
                    }

                    .course-controls {
                        padding: 20px 24px 16px;
                        border-bottom: 1px solid ${COLORS.border};
                        background: ${COLORS.white};
                    }

                    .course-search-input {
                        width: 100%;
                        max-width: 520px;
                        height: 42px;
                        border-radius: 9px !important;
                        border:
                            1px solid ${COLORS.border} !important;
                        background: #FBFCFD !important;
                        color: ${COLORS.navy};
                        transition:
                            all 0.2s ease;
                    }

                    .course-search-input:hover {
                        border-color:
                            ${COLORS.primary} !important;
                    }

                    .course-search-input:focus,
                    .course-search-input.ant-input-affix-wrapper-focused {
                        border-color:
                            ${COLORS.primary} !important;
                        box-shadow:
                            0 0 0 3px
                            rgba(15, 91, 120, 0.10) !important;
                    }

                    .course-search-input input {
                        background: transparent !important;
                        font-size: 13px;
                    }

                    .course-search-input input::placeholder {
                        color: #8A9AA3;
                    }

                    .course-summary {
                        min-height: 42px;
                        display: flex;
                        align-items: center;
                        justify-content: flex-end;
                        gap: 6px;
                        color: ${COLORS.muted};
                        font-size: 13px;
                    }

                    .course-summary strong {
                        color: ${COLORS.primary};
                        font-size: 16px;
                    }

                    .summary-dot {
                        width: 8px;
                        height: 8px;
                        border-radius: 50%;
                        background: ${COLORS.primary};
                        box-shadow:
                            0 0 0 4px
                            rgba(15, 91, 120, 0.10);
                        margin-right: 4px;
                    }

                    .course-table-container {
                        padding: 0 24px 20px;
                        background: ${COLORS.white};
                    }

                    .course-table-container .ant-table {
                        color: ${COLORS.navy};
                    }

                    .course-table-container
                    .ant-table-container {
                        border-radius: 10px;
                        overflow: hidden;
                        border:
                            1px solid ${COLORS.border};
                    }

                    .ant-table-thead > tr > th {
                        background:
                            ${COLORS.black} !important;
                        color:
                            #F4FAFC !important;
                        font-weight:
                            600 !important;
                        font-size:
                            12px !important;
                        text-transform:
                            uppercase;
                        letter-spacing:
                            0.45px;
                        border-bottom:
                            none !important;
                        padding:
                            13px 14px !important;
                    }

                    .ant-table-thead > tr > th::before {
                        display: none !important;
                    }

                    .ant-table-tbody > tr.table-row-light {
                        background:
                            #FAFCFD;
                    }

                    .ant-table-tbody > tr.table-row-white {
                        background:
                            #FFFFFF;
                    }

                    .ant-table-tbody > tr > td {
                        border-bottom:
                            1px solid #E8EEF1 !important;
                        padding:
                            13px 14px !important;
                        vertical-align:
                            middle;
                    }

                    .ant-table-tbody > tr:hover > td {
                        background:
                            #EDF6F8 !important;
                    }

                    .course-name-wrapper {
                        display: flex;
                        align-items: center;
                        gap: 11px;
                        min-width: 190px;
                    }

                    .course-icon-box {
                        width: 37px;
                        height: 37px;
                        flex: 0 0 37px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 9px;
                        color: ${COLORS.primary};
                        background:
                            ${COLORS.lightBlue};
                        border:
                            1px solid #D5E8EE;
                        font-size: 17px;
                    }

                    .course-name-content {
                        display: flex;
                        flex-direction: column;
                        min-width: 0;
                    }

                    .course-name-text {
                        color: ${COLORS.navy} !important;
                        font-size: 13px;
                        line-height: 1.35;
                    }

                    .course-sub-label {
                        color: ${COLORS.muted} !important;
                        font-size: 10px;
                        margin-top: 2px;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                    }

                    .division-tag {
                        margin: 0 !important;
                        border-radius: 7px !important;
                        padding:
                            4px 10px !important;
                        font-weight:
                            600 !important;
                        color:
                            ${COLORS.primary} !important;
                        background:
                            #EAF4F7 !important;
                        border:
                            1px solid #CFE4EA !important;
                    }

                    .division-tag .anticon {
                        color:
                            ${COLORS.primary};
                    }

                    .no-division-tag {
                        margin: 0 !important;
                        border-radius: 7px !important;
                        padding:
                            4px 10px !important;
                        color:
                            ${COLORS.muted} !important;
                        background:
                            #F1F4F5 !important;
                        border:
                            1px solid ${COLORS.border} !important;
                    }

                    .delete-course-button {
                        border-radius: 7px !important;
                        font-weight: 600;
                        height: 34px;
                        box-shadow: none !important;
                    }

                    .delete-course-button:hover {
                        transform: translateY(-1px);
                    }

                    .empty-description {
                        color:
                            ${COLORS.muted};
                    }

                    .ant-table-pagination {
                        margin-bottom: 0 !important;
                    }

                    .ant-pagination-item-active {
                        border-color:
                            ${COLORS.primary} !important;
                    }

                    .ant-pagination-item-active a {
                        color:
                            ${COLORS.primary} !important;
                    }

                    .ant-pagination-item:hover {
                        border-color:
                            ${COLORS.primary} !important;
                    }

                    .ant-pagination-item:hover a {
                        color:
                            ${COLORS.primary} !important;
                    }

                    .ant-select-selector:hover {
                        border-color:
                            ${COLORS.primary} !important;
                    }

                    .ant-select-focused
                    .ant-select-selector {
                        border-color:
                            ${COLORS.primary} !important;
                        box-shadow:
                            0 0 0 2px
                            rgba(15,91,120,0.08) !important;
                    }

                    @keyframes courseHeaderStripes {
                        0% {
                            transform:
                                translate3d(-4%, -2%, 0)
                                rotate(-5deg);
                        }

                        50% {
                            transform:
                                translate3d(3%, 2%, 0)
                                rotate(-5deg);
                        }

                        100% {
                            transform:
                                translate3d(-4%, -2%, 0)
                                rotate(-5deg);
                        }
                    }

                    @media (max-width: 899px) {

                        .course-page-container {
                            padding: 18px;
                        }

                        .course-header-content {
                            padding:
                                20px 22px;
                        }

                        .course-header-stat {
                            min-width: 135px;
                        }

                        .course-controls {
                            padding:
                                18px 20px 14px;
                        }

                        .course-table-container {
                            padding:
                                0 20px 18px;
                        }

                        .course-summary {
                            justify-content:
                                flex-start;
                        }

                    }

                    @media (max-width: 560px) {

                        .course-page-container {
                            padding: 10px;
                        }

                        .course-main-card {
                            border-radius:
                                12px !important;
                        }

                        .course-header-content {
                            min-height:
                                auto;
                            padding:
                                18px 17px;
                            align-items:
                                flex-start;
                        }

                        .course-header-left {
                            gap: 11px;
                        }

                        .course-header-icon {
                            width: 42px;
                            height: 42px;
                            flex-basis: 42px;
                            border-radius: 10px;
                            font-size: 20px;
                        }

                        .course-page-title {
                            font-size:
                                20px !important;
                        }

                        .course-page-subtitle {
                            font-size:
                                11px;
                            line-height:
                                1.45;
                        }

                        .course-header-stat {
                            display: none;
                        }

                        .course-controls {
                            padding:
                                14px 14px 12px;
                        }

                        .course-table-container {
                            padding:
                                0 14px 14px;
                        }

                        .course-search-input {
                            max-width:
                                100%;
                            height:
                                40px;
                        }

                        .course-summary {
                            min-height:
                                30px;
                            font-size:
                                12px;
                        }

                        .ant-table {
                            font-size:
                                12px;
                        }

                        .ant-table-thead > tr > th {
                            font-size:
                                11px !important;
                            padding:
                                11px 10px !important;
                        }

                        .ant-table-tbody > tr > td {
                            padding:
                                10px !important;
                        }

                        .course-name-wrapper {
                            gap:
                                8px;
                        }

                        .course-icon-box {
                            width:
                                32px;
                            height:
                                32px;
                            flex-basis:
                                32px;
                            font-size:
                                14px;
                        }

                        .course-name-text {
                            font-size:
                                12px;
                        }

                        .division-tag,
                        .no-division-tag {
                            font-size:
                                11px;
                            padding:
                                3px 8px !important;
                        }

                        .delete-course-button {
                            height:
                                31px;
                            padding:
                                0 9px !important;
                            font-size:
                                11px;
                        }

                    }

                    @media (prefers-reduced-motion: reduce) {

                        .course-header::before {
                            animation:
                                none;
                        }

                        .delete-course-button:hover {
                            transform:
                                none;
                        }

                    }
                `}
            </style>
        </div>
    );
};

export default SubjectViewList;

